import jsPDF from "jspdf";
import { format } from "date-fns";
import qrCode from "../assets/images/qr.png";
import { getSizeType } from "./itemUtils";

export const generateInvoice = (order) => {
  // 58mm width, variable height (start with ample 300mm to avoid cutting, users can cut manually or printer handles it)
  // Standard thermal receipts are one long page.
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [58, 300]
  });

  const pageWidth = 58;
  const margin = 2; // Very narrow margin for 58mm
  const contentWidth = pageWidth - (margin * 2);
  let yPos = 5;
  const lineHeight = 3.5;

  // Font settings
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);

  // Helper to center text
  const centerText = (text, y) => {
    const textWidth = doc.getTextWidth(text);
    const x = (pageWidth - textWidth) / 2;
    doc.text(text, x, y);
  };

  // Helper to add a dashed line
  const addDashedLine = (y) => {
    doc.setLineDash([1, 1], 0);
    doc.line(margin, y, pageWidth - margin, y);
    doc.setLineDash([]); // Reset
    return y + 5;
  };

  // --- HEADER ---
  centerText("PAID", yPos);
  yPos += lineHeight;

  doc.setFontSize(11);
  centerText("Gutful Goodness", yPos);
  yPos += lineHeight + 1;

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  // Address can be added here if available in config
  // centerText("Store Address Line 1", yPos); 
  // yPos += lineHeight;

  yPos = addDashedLine(yPos + 2);

  // --- CUSTOMER DETAILS ---
  doc.setFontSize(8);
  doc.text(`Source: Website`, margin, yPos);
  yPos += lineHeight;

  const customerName = order.customerDetails?.name || 'Customer';
  doc.text(`Name: ${customerName}`, margin, yPos);
  yPos += lineHeight;

  if (order.customerDetails?.address) {
    const addressHeader = "Address: ";
    // Wrap address
    const addressLines = doc.splitTextToSize(addressHeader + order.customerDetails.address, contentWidth);
    doc.text(addressLines, margin, yPos);
    yPos += (addressLines.length * lineHeight);
  }

  if (order.customerDetails?.phone) {
    doc.text(`Phone: ${order.customerDetails.phone}`, margin, yPos);
    yPos += lineHeight;
  }

  yPos = addDashedLine(yPos + 1);

  // --- ORDER INFO ---
  const orderDate = order.createdAt ? format(new Date(order.createdAt), "dd/MM/yy HH:mm") : "N/A";
  doc.text(`Date: ${orderDate}`, margin, yPos);
  yPos += lineHeight;

  doc.text(`Order No: ${order.orderId}`, margin, yPos); // Using orderId as Bill No/Order No
  yPos += lineHeight;

  yPos = addDashedLine(yPos + 1);

  // --- ITEMS TABLE HEADER ---
  doc.setFont("helvetica", "bold");
  doc.text("Item", margin, yPos);
  doc.text("Qty", 38, yPos, { align: "right" }); // Adjust Qty position
  doc.text("Amt", pageWidth - margin, yPos, { align: "right" });
  yPos += lineHeight;
  yPos = addDashedLine(yPos - 1);

  // --- ITEMS LIST ---
  doc.setFont("helvetica", "normal");

  (order.items || []).forEach(item => {
    const itemName = item.name;
    const itemQty = item.quantity.toString();
    const itemTotal = (item.itemTotal || (item.price * item.quantity)).toFixed(2);

    // Widths logic:
    // Page: 58mm. Margins: 2mm left/right. Content: 54mm.
    // Qty and Avg need space.
    // Qty at 38mm right-aligned (approx column width 6mm)
    // Amt at 56mm right-aligned (approx column width 12mm)
    // Left side for Name: 0 to ~32mm width.
    const maxNameWidth = 30;

    const nameLines = doc.splitTextToSize(itemName, maxNameWidth);

    // Draw Name
    doc.text(nameLines, margin, yPos);

    // Draw Qty and Amt on the same Y as the first line of Name
    doc.text(itemQty, 38, yPos, { align: "right" });
    doc.text(itemTotal, pageWidth - margin, yPos, { align: "right" });

    yPos += (nameLines.length * lineHeight);

    // Portions or Addons
    if (item.selectedPortion) {
      doc.setFontSize(7);
      doc.text(`- Size: ${getSizeType(item.selectedPortion, item)}`, margin + 2, yPos);
      yPos += 3;
      doc.setFontSize(8);
    }

    if (item.addons && item.addons.length > 0) {
      doc.setFontSize(7);
      item.addons.forEach(addon => {
        doc.text(`+ ${addon.name}`, margin + 2, yPos);
        yPos += 3;
      });
      doc.setFontSize(8);
    }

    yPos += 1; // Extra space between items
  });

  yPos = addDashedLine(yPos);

  // --- TOTALS ---
  const pricing = order.pricing || {};

  const drawRow = (label, text, bold = false) => {
    if (bold) doc.setFont("helvetica", "bold");
    else doc.setFont("helvetica", "normal");

    doc.text(label, 30, yPos, { align: "right" }); // Label Anchor
    doc.text(text, pageWidth - margin, yPos, { align: "right" }); // Value Anchor
    yPos += lineHeight;
  };

  // Total Quantity logic if needed
  const totalQty = (order.items || []).reduce((acc, curr) => acc + curr.quantity, 0);
  drawRow("Total Qty:", totalQty.toString());

  drawRow("Sub Total:", (pricing.subtotal || 0).toFixed(2));

  if (pricing.deliveryFee > 0) {
    drawRow("Delivery:", (pricing.deliveryFee).toFixed(2));
  }

  if (pricing.discount > 0) {
    drawRow("Discount:", `-${(pricing.discount).toFixed(2)}`);
  }

  yPos += 1; // Spacer
  doc.setFontSize(11);
  drawRow("Grand Total:", `Rs ${(pricing.total || 0).toFixed(2)}`, true);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  yPos += 2;

  // --- PAYMENT INFO ---
  const paymentMethod = order.paymentMethod ? order.paymentMethod.toUpperCase() : "COD";
  doc.text(`Paid via ${paymentMethod}`, margin, yPos);

  // Status check
  if (order.paymentStatus) {
    doc.text(paymentMethod === "COD" ? `UNPAID` : `[${order.paymentStatus.toUpperCase()}]`, pageWidth - margin, yPos, { align: "right" });
  }
  yPos += lineHeight;

  yPos = addDashedLine(yPos + 1);

  // --- FOOTER & QR CODE ---
  centerText("Scan to Check Order", yPos);
  yPos += lineHeight + 2;

  // Add QR Code Image
  // Size: 25mm x 25mm (Square)
  // Centered: (58 - 25) / 2 = 16.5
  try {
    doc.addImage(qrCode, 'PNG', 16.5, yPos, 25, 25);
    yPos += 27; // Move past image
  } catch (e) {
    console.error("Error adding QR code image", e);
    doc.text("[QR Code Error]", margin, yPos);
    yPos += lineHeight;
  }

  doc.setFontSize(7);
  centerText(order.orderId, yPos + 3); // Text under barcode

  yPos += 8;
  doc.setFontSize(8);
  centerText("Thank You!", yPos);

  doc.save(`Invoice_${order.orderId}.pdf`);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};
